import { DeepPartial, KeyOfType, MaybeNull, NonNull } from "tsdef";

export type ExtractActionsFromState<D> = ({ [P in keyof D]: P extends string ? { type: P, value: D[P] } : never })[keyof D]
export type ExtractSettersFromState<D> = ({ [P in keyof D]: P extends string ? { type: `set${Capitalize<P>}`, value: D[P] } : never })[keyof D]

export type ExtractPrefixed<S extends string, P extends string> = S extends `${P}${infer A}` ? `${Uncapitalize<A>}` : never;


export type Override<T1, T2> = Omit<T1, keyof T2> & T2;
export type ArrayOverride<T1, T2> = Array<Override<T1, T2>>;
export type GraphType<T extends { __typeName: string }> = Pick<T, '__typeName'>
export type PickGraphType<T extends { __typeName: string }, K extends keyof T> = Omit<Pick<T, K>, '__typeName'> & GraphType<T>

export type KeyOfGraphType<T> = KeyOfType<T, { __typeName: string }> | KeyOfType<T, Array<{ __typeName: string }>>;

export type DeepPartialGraphArray<TArray extends Array<TValue>, TValue extends { __typeName: string }> =
    TArray extends NonNullable<TArray>
    ? Array<DeepPartialGraphValue<TValue>>
    : MaybeNull<Array<DeepPartialGraphValue<TValue>>>;

export type DeepPartialGraphValue<T extends { __typeName: string }> =
    T extends NonNullable<T>
    ? DeepPartialGraph<T>
    : MaybeNull<DeepPartialGraph<NonNull<T>>>;

export type DeepPartialGraph<T extends { __typeName: string } | Array<{ __typeName: string }>> = {
    [P in KeyOfGraphType<T>]?:
    T[P] extends Array<infer I>
    ? I extends { __typeName: string } ? DeepPartialGraphArray<T[P], I> : never
    : T[P] extends { __typeName: string } ? DeepPartialGraphValue<T[P]> : never
} & {
        [P in keyof Omit<T, KeyOfGraphType<T>>]?: T[P];
    };

export type RequireKeys<T, K extends keyof T> =
    Omit<T, K> & { [P in K]-?: NonNullable<T[P]> };

//export type DeepPartialGraph0<T> = Partial<Omit<T, KeysOfType<T, { typeName: string }>>> & DeepPartialGraph<Pick<T, KeysOfType<T, { typeName: string }>>;

//export type DeepPartialOverride<T, U extends DeepPartial<T>> = Omit<T, keyof U> & U
export type GraphOutput = { __typeName: string }

export type DeepPartialOverride<T, U extends DeepPartial<T>> = Omit<DeepPartial<T>, keyof (U)> & U
export type DeepPartialListedAndOverride<T, K extends keyof T, U extends DeepPartial<T>> = Omit<T, K | keyof U> & Pick<DeepPartial<T>, K> & U
export type DeepPartialNotListedAndOverride<T, K extends keyof T, U extends DeepPartial<Omit<T, K>>> = Pick<T, K> & Omit<DeepPartial<T>, K & keyof U> & U

export type DeepPartialListed<T, K extends keyof T> = DeepPartialListedAndOverride<T, K, {}>
export type DeepPartialNotListed<T, K extends keyof T> = DeepPartialNotListedAndOverride<T, K, {}>

export type GraphDeepPartial<T extends GraphOutput> = GraphDeepPartialNotListedAndOverride<T, keyof {}, {}>

export type GraphDeepPartialOverride<T extends GraphOutput, U extends Omit<DeepPartial<T>, keyof GraphOutput>> = Omit<DeepPartial<T>, keyof (GraphOutput & U)> & Pick<T, keyof GraphOutput> & U
export type GraphDeepPartialListedAndOverride<T extends GraphOutput, K extends keyof Omit<T, keyof GraphOutput>, U extends DeepPartial<Omit<T, K | keyof GraphOutput>>> = Omit<T, K | keyof U> & Pick<DeepPartial<T>, K> & U

export type GraphDeepPartialNotListedAndOverride<T extends GraphOutput, K extends keyof Omit<T, keyof GraphOutput>, U extends DeepPartial<Omit<T, K | keyof GraphOutput>>> = Pick<T, K | keyof GraphOutput> & Omit<DeepPartial<T>, K & keyof U> & U

export type GraphDeepPartialListed<T extends GraphOutput, K extends keyof Omit<T, keyof GraphOutput>> = GraphDeepPartialListedAndOverride<T, K, {}>
export type GraphDeepPartialNotListed<T extends GraphOutput, K extends keyof Omit<T, keyof GraphOutput>> = GraphDeepPartialNotListedAndOverride<T, K, {}>

export type PackingGroup = '' | 'I' | 'II' | 'III';



export type User = {
    id?: number,
    login?: string,
    email?: string,
    displayName?: string | null,
    terminals?: Terminal[],
    roles?: Role[],
    memberships?: Membership[]
}

export type Role = {
    id?: number,
    name?: RoleName,
    group: Group,
    users: User[]
}


export enum RoleName {
    SUPERUSER = 'superuser',
    ADMIN = 'admin',
    USER = 'user',
    OPERATIVE_MANAGER = 'operativeManager',
    QSA = 'qsa',
    DOCUMENTATION = 'documentation'
}


export type Group = {
    id?: number,
    name?: string,
    roles?: Role[],
    terminalNames?: string[] | null,
    portCodes?: string[] | null
}

export type Membership = {
    user?: User,
    roles?: Role[],
    group?: Group
}

export type SignInCredentials = {
    loginOrEmail: string,
    password: string,
    rememberMe: boolean | null
}

export type MutationResult = {
    isOk?: boolean
}

export type TypedMutationResult<T> = MutationResult & {
    data?: T
}

export type SignInResult = TypedMutationResult<User | null>

export type SignOutResult = MutationResult

export type DangerousGoods = {
    id?: number,
    unNumber?: string,
    packingGroup?: string,
    weight?: number,
    packaging?: string | null,
    numberOfPackages?: null | number,
    provision274?: string,
    flashPoint?: string,
    adrClass?: string,
    adrSecondaryClasses?: string[],
    marinePollutant?: boolean,
    limitedQuantity?: boolean,
    fumigated?: boolean,
    coolingAgents?: boolean,
    elevatedTemperature?: boolean,
    safetyDataSheet?: Partial<HandlingAttachment> | null,
    emergencyCardUrl?: string
}

export enum HandlingType {
    None = 'NONE',
    Transit = 'TRANSIT',
    Boarding = 'BOARDING',
    Unboarding = 'UNBOARDING',
    TranshipmentBoarding = 'TRANSHIPMENT_BOARDING',
    TranshipmentUnboarding = 'TRANSHIPMENT_UNBOARDING',
}

export type Handling = {
    id?: number,
    terminalName?: string,
    creationDate?: Date,
    loadingUnitIdentifier?: string,
    checkedIn?: boolean,
    checkedOut?: boolean,
    checkInDate?: Date | null,
    checkOutDate?: Date | null,
    area?: string | null,
    bay?: string | null,
    row?: string | null,
    tier?: string | null,
    loadingUnitSize?: string | null,
    loadingUnitSizeFromGate?: string | null,
    shippingAgencyOrCarrier?: null | string,
    ship?: string | null,
    unboardingShip?: string | null,
    boardingShip?: string | null,
    type?: HandlingType,
    voyage?: string | null,
    equipmentType?: string | null,
    direct?: boolean,
    approvedByTerminal?: boolean,
    checkedByTerminal?: boolean,
    dangerousGoods?: DangerousGoods[],
    harbourOfficeAuthorization?: HarbourOfficeAuthorization | null,
    portAuthorityAuthorization?: PortAuthorityAuthorization | null,
    prescriptions?: string | null,
    boardingPrescriptions?: string | null,
    unboardingPrescriptions?: string | null,
    booking?: string | null,
    bookingFromTerminal?: string | null,
    boardingShipFromTerminal?: string | null,
    unboardingShipFromTerminal?: string | null,
    qsaNotes?: string | null,
    cei?: number | null,
    fei?: number | null,
    idSerchim?: number | null,
    goodsDataModified?: boolean | null,
    goodsDataModifiedAt?: string | null,
    prescriptionsModified?: boolean | null,
    prescriptionsModifiedAt?: string | null,
    standardDistance?: number | null,
    chemistDocuments?: HandlingAttachment[] | null,
    assignedDate?: string | null,
    requestedDate?: string | null,
    storageDays?: number | null,
    terminalVoyageEta?: Date | null,
}

export type HandlingAttachment = {
    id?: number,
    loadingUnit?: string,
    shippingAgencyOrCarrier?: string,
    voyage?: string,
    ship?: string,
    documentType?: HandlingAttachmentDocumentType,
    documentName?: string,
    documentPath?: string,
    handlingId?: number | null,
    dangerousGoodsId?: number | null,
    documentTitle?: string | null,
    downloadUrl?: string
}

export enum HandlingAttachmentDocumentType {
    Multimodal = 'MULTIMODAL',
    SafetyDataSheet = 'SAFETY_DATA_SHEET'
}

export type HarbourOfficeAuthorization = {
    handlingId?: string | null,
    creationDate?: Date | null,
    authorization?: HarbourOfficeAuthorizationType,
    approvalId?: string | null
}

export enum HarbourOfficeAuthorizationType {
    None = 'NONE',
    Transit = 'TRANSIT',
    Boarding = 'BOARDING',
    Unboarding = 'UNBOARDING'
}

export type Prop<T> = keyof T;

export type HandlingProp = Prop<Handling>;

export type PortAuthorityAuthorization = {
    id?: number,
    fileNumber?: number,
    dispatchDate?: Date | null,
    authorized?: boolean,
    authorizationDate?: Date | null,
    downloadUrl?: string
}

export type Terminal = {
    id?: number,
    name?: string,
    slug?: string,
    port?: string,
    portCode?: string,
    active?: boolean,
    maxCei?: number | null,
    maxFei?: number | null,
    maxRadius?: number | null,
    teusLimit?: number | null,
    dataUpdate?: DataUpdate,
    areas?: TerminalArea[],
    riskAssessments?: RiskAssessment[],
    segregations?: Segregation[],
    handlings?: Handling[],
    centerLatitude?: number | null,
    centerLongitude?: number | null,
    mapZoom?: number | null,
    northLatitude?: number | null,
    southLatitude?: number | null,
    westLongitude?: number | null,
    eastLongitude?: number | null,
}

export type TerminalArea = {
    id?: number | null,
    terminalId?: number,
    name?: string,
    description?: string | null,
    centerLatitude?: number,
    centerLongitude?: number,
    warningThreshold?: number | null,
    alertThreshold?: number | null,
    alarmThreshold?: number | null
}

export type DataUpdate = {
    id?: number,
    type?: string,
    name?: string,
    port?: string,
    lastUpdate?: string
}

export type RiskAssessment = {
    id?: number,
    terminalName?: string,
    area?: string,
    maxFei?: number | null,
    feiBay?: string | null,
    feiRow?: string | null,
    maxCei?: number | null,
    ceiBay?: string | null,
    ceiRow?: string | null,
    radius?: number | null,
    radius2?: number | null,
    radiusBay?: string | null,
    radiusRow?: string | null,
    radiusF2?: string | null,
    radius2F2?: string | null,
    risk?: string | null,
    risk2?: string | null,
    radiusR2?: string | null,
    radius2R2?: string | null,
    radiusF2R2?: string | null,
    radius2F2R2?: string | null,
    result?: boolean | null,
    error?: string | null,
    highRadius?: boolean
}

export type Segregation = {
    class2?: string,
    id?: number,
    loadingUnit1Identifier?: string,
    loadingUnit2Identifier?: string,
    class1?: string,
    row1?: string,
    row2?: string,
    bay1?: string,
    bay2?: string,
    area?: string,
    terminalName?: string,
    dateOfInput?: Date
}

export type GraphQueryResponse<T> = {
    data: T,
    errors?: any | null,
    query?: string,
    document?: any,
    operation?: any,
    perf?: any[],
    extensions?: any
}

export type HacpackQueryResponse = {
    terminals?: Terminal[],
    terminal?: Terminal,
    handling?: Handling,
    currentUser?: User | null
}

export type HacpackMutationResponse = {
    signIn?: SignInResult,
    signOut?: SignOutResult,
    updateTerminalMapView?: Terminal | null,
    addTerminalArea?: TerminalArea | null,
    updateTerminalArea?: TerminalArea | null,
    deleteTerminalArea?: MutationResult
}